import { useTranslation } from 'react-i18next';
import { AUDIO_UPLOAD } from '@/shared/constants';
import speakerIcon from '@/assets/svg/speaker_icon.svg';
import { AudioPlayer } from './AudioPlayer';
import { TranscriptionEditor } from './TranscriptionEditor';
import { countWords } from '../utils/audioDescription';
import type { AudioDescriptionForm } from '../hooks/useAudioDescriptionForm';

interface AudioDescriptionSectionProps {
  form: AudioDescriptionForm;
  exhibitionColor?: string;
}

/**
 * Onglet « Audiodescription de l'oeuvre » (Figma admin 324:5870) : conseils, fichier
 * audio, transcription (obligatoire pour publier) et nombre de mots.
 */
export const AudioDescriptionSection: React.FC<AudioDescriptionSectionProps> = ({
  form,
  exhibitionColor,
}) => {
  const { t } = useTranslation();
  const wordCount = countWords(form.transcription);

  return (
    // Titre et introduction : en-tête de l'onglet (CreateWorkModal).
    <div className="create-work-section audio-description-section">

      <details className="audio-description-section__tips" open>
        <summary>{t('exhibitions.audioDescription.tipsTitle')}</summary>
        <p>{t('exhibitions.audioDescription.tipsStart')}</p>
        <p>{t('exhibitions.audioDescription.tipsDescribe')}</p>
      </details>

      <h4 className="section-label">{t('exhibitions.audioDescription.audioLabel')}</h4>
      <input
        ref={form.inputRef}
        type="file"
        accept={AUDIO_UPLOAD.ACCEPT}
        onChange={form.handleSelect}
        style={{ display: 'none' }}
        aria-label={t('exhibitions.audioDescription.audioLabel')}
      />
      {form.preview ? (
        <AudioPlayer audioUrl={form.preview} onRemove={form.handleRemove} exhibitionColor={exhibitionColor} />
      ) : (
        <>
          <button type="button" className="upload-box audio-upload" onClick={form.openFilePicker}>
            <img src={speakerIcon} alt="" className="audio-icon" />
            <span className="upload-audio-text">
              <span>{t('exhibitions.audioUploadPrompt')}</span>
              <span className="upload-audio-hint">
                {t('exhibitions.audioUploadFormatsMax', { maxSize: AUDIO_UPLOAD.MAX_SIZE_MB })}
              </span>
            </span>
          </button>
          {form.uploadError && (
            <p className="upload-audio-error" role="alert">
              {form.uploadError}
            </p>
          )}
        </>
      )}

      <h4 className="section-label section-label--with-hint">{t('exhibitions.audioDescription.transcriptionLabel')}</h4>
      <p className="create-work-hint">{t('exhibitions.audioDescription.transcriptionHint')}</p>
      <TranscriptionEditor
        value={form.transcription}
        onChange={form.setTranscription}
        exhibitionColor={exhibitionColor}
      />
      <p className="create-work-word-count" aria-live="polite">
        {t('exhibitions.audioDescription.wordCount', { count: wordCount })}
      </p>
    </div>
  );
};
